import type { JSX } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useSelector } from 'react-redux';
import { selectUser } from 'store/slices/auth';

/**
 * @description Authentication guard component that redirects unauthenticated users to the login page.
 * Checks the authentication status using AWS Amplify Authenticator context.
 *
 * @SADtrace SAD-003-1, SAD-003-1.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 * 06-29-2026 aleksandar dukleski: trust restored Redux session so a browser refresh keeps the current page
 *
 * @param {Object} props - Component props
 * @param {JSX.Element} props.children - Child components to render if authenticated
 *
 * @returns {JSX.Element} Either the children or a redirect to login page
 */
const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const { route } = useAuthenticator((context) => [context.route]);
  const user = useSelector(selectUser);

  // Redirect to /login only when Amplify is not authenticated AND there is no
  // restored session. On refresh, App's checkSession() repopulates `user` before
  // the router mounts, so we must not bounce to /login while Amplify's route is
  // still catching up — that bounce is what wrongly sent refreshes to the role
  // home page (regression after DLA-1502). With a restored session we render the
  // current page, so a refresh keeps the user where they were.
  if (route !== 'authenticated' && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireAuth;
